import type React from "react";

import { twResolve } from "@/helpers/tw-resolve";

import { type ButtonBaseProps, type ButtonButtonProps, type ButtonSize, buttonVariants } from "./Button";
import { ButtonLoader } from "./ButtonLoader";

type ToggleButtonStateConfig = {
  label: string;
  icon?: React.ReactNode;
};

export type ToggleButtonProps = Omit<
  ButtonBaseProps & ButtonButtonProps,
  "type" | "children" | "title" | "isCircular" | "underline" | "emphasis"
> & {
  size?: ButtonSize;
  config: {
    checked: ToggleButtonStateConfig;
    unchecked: ToggleButtonStateConfig;
  };
  isChecked: boolean | undefined;
};

export function ToggleButton({
  config,
  "data-testid": dataTestId,
  isChecked,
  isLoading,
  disabled,
  size = "md",
  fullWidth = false,
  onClick,
  ...otherProps
}: ToggleButtonProps): React.ReactNode {
  const placeholderState = config.checked.label.length > config.unchecked.label.length ? "checked" : "unchecked";
  const overlayState = placeholderState === "checked" ? "unchecked" : "checked";

  return (
    <button
      {...{ onClick, ...otherProps }}
      type="button"
      aria-label={config[isChecked ? "checked" : "unchecked"].label}
      disabled={disabled || isLoading}
      className={twResolve(
        buttonVariants({ variant: isChecked ? "filled" : "soft", size, fullWidth: fullWidth === true }),
        size === "sm" && "h-toggle-button-height-sm gap-space-sm px-toggle-button-inline-sm text-caption-emphasis",
        fullWidth === true && "w-full",
        fullWidth === "untilSm" && "w-full @sm:w-auto",
      )}
      data-testid={dataTestId}
    >
      <ButtonLoader isVisible={isLoading} />
      <span
        aria-hidden="true"
        className={twResolve(
          "invisible flex h-full items-center gap-space-sm opacity-0",
          ((placeholderState === "checked" && isChecked) || (placeholderState === "unchecked" && !isChecked)) &&
            "visible opacity-100",
        )}
      >
        {config[placeholderState].icon}
        <span className="whitespace-nowrap">{config[placeholderState].label}</span>
      </span>
      <span
        aria-hidden="true"
        className={twResolve(
          "invisible absolute top-1/2 left-1/2 flex h-full -translate-1/2 items-center gap-space-sm opacity-0",
          ((overlayState === "checked" && isChecked) || (overlayState === "unchecked" && !isChecked)) &&
            "visible opacity-100",
        )}
      >
        {config[overlayState].icon}
        <span className="whitespace-nowrap">{config[overlayState].label}</span>
      </span>
    </button>
  );
}
